import { inject, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

@Pipe({
	name: 'safeMarkdown',
	standalone: true,
	pure: true,
})
export class SafeMarkdownPipe implements PipeTransform {
	private readonly sanitizer = inject(DomSanitizer);

	public constructor() {
		marked.setOptions({ breaks: false, gfm: true });
	}

	public transform(markdown: string | null | undefined): SafeHtml {
		if (!markdown) {
			return '';
		}

		const normalized = this.collapseExportArtifactBlankLines(markdown);
		const rawHtml = marked.parse(normalized, { async: false }) as string;

		const cleanHtml = DOMPurify.sanitize(rawHtml, {
			//Whitelist to not allow tags like <script> <iframe> <form> ...
			ALLOWED_TAGS: [
				'p',
				'br',
				'hr',
				'h1',
				'h2',
				'h3',
				'h4',
				'h5',
				'h6',
				'strong',
				'em',
				's',
				'del',
				'mark',
				'sub',
				'sup',
				'ul',
				'ol',
				'li',
				'blockquote',
				'code',
				'pre',
				'table',
				'thead',
				'tbody',
				'tr',
				'th',
				'td',
				'a',
				'img',
				'span',
				'div',
			],
			ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'align'],
			ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
		});
		return this.sanitizer.sanitize(1 /* SecurityContext.HTML */, cleanHtml) ?? '';
	}

	/**
	 * Workaround for the wiki export: it inserts  blank line between
	 * every source line (e.g. "* Item 1\n\n* Item 2" instead of
	 * "* Item 1\n* Item 2"), which breaks marked's block detection
	 *
	 * Removes a blank line only when the line before and after share the
	 * same block type.
	 */
	private collapseExportArtifactBlankLines(markdown: string): string {
		const lines = markdown.split('\n');
		const result: string[] = [];

		for (let i = 0; i < lines.length; i++) {
			const line = lines[i];
			const isBlank = line.trim() === '';

			if (isBlank) {
				const prevLine = this.lastNonBlank(result, result.length - 1);
				const nextLine = this.firstNonBlank(lines, i + 1);

				const prevType = prevLine ? this.blockType(prevLine) : null;
				const nextType = nextLine ? this.blockType(nextLine) : null;

				if (prevType !== null && prevType === nextType) {
					continue;
				}
			}

			result.push(line);
		}

		return result.join('\n');
	}

	/** Returns the block type of a line based on its prefix, or null. */
	private blockType(line: string): 'table' | 'quote' | 'ul' | 'ol' | null {
		const trimmed = line.replace(/^\s+/, '');
		if (/^\|.*\|\s*$/.test(trimmed)) return 'table';
		if (/^>/.test(trimmed)) return 'quote';
		if (/^[*+-]\s+/.test(trimmed)) return 'ul';
		if (/^\d+[.)]\s+/.test(trimmed)) return 'ol';
		return null;
	}

	private lastNonBlank(lines: string[], fromIdx: number): string | null {
		for (let i = fromIdx; i >= 0; i--) {
			if (lines[i].trim() !== '') return lines[i];
		}
		return null;
	}

	private firstNonBlank(lines: string[], fromIdx: number): string | null {
		for (let i = fromIdx; i < lines.length; i++) {
			if (lines[i].trim() !== '') return lines[i];
		}
		return null;
	}
}
